import { Button } from '@creditornot/cb-button';
import { FormRow, FormRowProps } from '@creditornot/cb-form';
import { Plus } from '@creditornot/cb-icons';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useOfferTabContext } from '../../context/OfferTabContext';

export type WithCreateTagFormRowProps = FormRowProps & {
  shouldDisplayCreateTag?: boolean;
  /**
   * Used to highlight errors on the input fields when user tries to save the offer
   * (e.g: name and price) we want to show an error and highlight the field instead of the `+ Add` button
   */
  shouldDisplayEditByDefault?: boolean;
};

export const WithCreateTagFormRow = ({
  shouldDisplayCreateTag,
  label,
  shouldDisplayEditByDefault,
  children,
  ...rest
}: WithCreateTagFormRowProps) => {
  const [showAddButton, setShowAddButton] = useState(shouldDisplayCreateTag);
  const firstIsInitialEmptyMemo = useRef(shouldDisplayCreateTag);
  const hasUpdatedOnce = useRef(false);
  const { isShowingTabErrors } = useOfferTabContext();

  const fieldHasHighlightedError = isShowingTabErrors && shouldDisplayEditByDefault;

  useEffect(() => {
    /**
     * NOTE: there's a state race condition that makes the `isInitialEmpty` be updated with the correct value only after this component is mounted
     * This boolean value depends on a combination of `form.values` which only gets updated with the new `Offer` data after this component is mounted
     * When this happens, we do an update to the `addButton` initial state to be reliant on the correct value, just once
     * Using a conditional render instead would result in an undesired flickering effect
     */
    const hasIsInitialEmptyUpdatedAfterMount = firstIsInitialEmptyMemo.current !== shouldDisplayCreateTag;

    if (hasUpdatedOnce.current || !hasIsInitialEmptyUpdatedAfterMount) {
      return;
    }

    setShowAddButton(shouldDisplayCreateTag);
    hasUpdatedOnce.current = true;
  }, [shouldDisplayCreateTag]);

  return (
    <FormRow label={label} {...rest}>
      {showAddButton && !fieldHasHighlightedError ? (
        <Button
          icon={<Plus />}
          iconPosition="left"
          onClick={() => setShowAddButton(false)}
          size="tiny"
          variant="lightBlue"
        >
          <FormattedMessage id="shared-menu.editor.offer-panel.empty-field-add-button" values={{ label }} />
        </Button>
      ) : (
        children
      )}
    </FormRow>
  );
};
